/**
 * ソーシャルリンク表示コンポーネント
 * プロフィールページでユーザーのソーシャルリンクを表示します。
 * 最初の数個のみを表示し、残りは「+N」ボタンでダイアログに全表示します。
 *
 * Note: ソーシャルプラットフォームの定数定義は constants/socialPlatform.tsx を参照
 */
import React, { useState } from 'react';
import { Box, Chip, Link, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { SOCIAL_PLATFORMS } from '../../constants/socialPlatform';
import { SocialLinks as SocialLinksType } from '../../types/UserProfile';

interface SocialLinksProps {
  socialLinks: SocialLinksType;
  maxVisible?: number;
  onCopy?: (message: string) => void;
  /**
   * ラベル(ユーザーID)を出さずアイコンだけにする省スペース表示。
   * 通常表示は label にIDをそのまま出すため 1個 100px 前後になり、
   * 3個並ぶと 300px を超えてモバイル幅では成立しない。
   * ダイアログ内は常にラベル付き(そこは幅がある)。
   */
  iconOnly?: boolean;
  /**
   * アイコンにアクセント色を乗せる（アクセントの適用段階が「強め」のとき）。
   * ラベル(ID)は色を変えない。暗いアクセント色を選ばれると読めなくなるため。
   */
  accentIcons?: boolean;
  /**
   * 見本として出す。**押せなくすること。**
   *
   * 装飾パネルで「アクセントスタイル」を試すとき、SNS を未設定の人には
   * 色が乗る先が存在せず、切り替えても何も起きない。そこで中身のない見本を出す。
   * 見え方を確かめるのが目的なので**本物と同じ見た目にする**（薄くしたりしない）。
   * ただしリンク先もIDも実在しないので、遷移とコピーだけ殺す。
   */
  sample?: boolean;
}

/** 見本用のダミー。実在しない値であることが分かる文字にすること */
export const SAMPLE_SOCIAL_LINKS: SocialLinksType = { x: 'sample', yt: 'sample', stm: 'sample' };

const SocialLinks: React.FC<SocialLinksProps> = ({
  socialLinks,
  maxVisible = 3,
  onCopy,
  iconOnly = false,
  accentIcons = false,
  sample = false
}) => {
  const iconColor = accentIcons ? 'primary.main' : 'text.secondary';
  const [dialogOpen, setDialogOpen] = useState(false);

  const activePlatforms = SOCIAL_PLATFORMS.filter(platform => socialLinks?.[platform.key]);
  const visiblePlatforms = activePlatforms.slice(0, maxVisible);
  const hiddenCount = activePlatforms.length - maxVisible;

  const renderChip = (platform: typeof SOCIAL_PLATFORMS[0], value: string, isInDialog = false) => {
    const url = platform.urlTemplate(value);

    const handleCopyId = () => {
      if (!url) {
        navigator.clipboard.writeText(value);
        onCopy?.('IDがコピーされました');
      }
    };

    // ダイアログ内は幅があるので常にラベル付き
    const compact = iconOnly && !isInDialog;

    const chipElement = (
      <Chip
        key={platform.key}
        icon={platform.icon as React.ReactElement}
        label={compact ? undefined : value}
        size="small"
        sx={{
          height: isInDialog ? '28px' : '24px',
          fontSize: isInDialog ? '0.75rem' : '0.7rem',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          color: 'text.secondary',
          ...(isInDialog && {
            width: '100%',
            justifyContent: 'flex-start',
          }),
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'primary.main',
          },
          '& .MuiChip-icon': {
            marginLeft: isInDialog ? '8px' : '6px',
            color: iconColor
          },
          ...(isInDialog && {
            '& .MuiChip-label': {
              paddingLeft: '8px',
              paddingRight: '12px',
            }
          }),
          // アイコンのみ表示。上の .MuiChip-icon の margin を打ち消す必要があるので
          // 必ずこの位置(sx の最後)に置くこと。
          ...(compact && {
            width: '24px',
            '& .MuiChip-label': { display: 'none' },
            '& .MuiChip-icon': { margin: 0, color: iconColor },
          }),
          cursor: sample ? 'default' : 'pointer',
        }}
        onClick={!sample && !url ? handleCopyId : undefined}
      />
    );

    // 見本はリンクにしない。ダミーIDのURLへ飛ばしてしまう
    if (sample) return chipElement;

    return url ? (
      <Link
        key={platform.key}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ textDecoration: 'none' }}
      >
        {chipElement}
      </Link>
    ) : (
      chipElement
    );
  };

  if (activePlatforms.length === 0) {
    return null;
  }

  return (
    <>
      {/* 外側の余白は持たない。置き場所の都合は呼び出し側が決める。
          (以前ここに mb があり、バナー内で下端揃えにしたときアバターと 4px ずれていた)

          **折り返さない・縮まない。** 出るのは最大4個(可視3 + "+N")で、
          アイコン表示なら 108px しか要らない。隣に並ぶ場所タグのほうが
          可変長なので、幅が足りないときは場所を省略記号で切らせる側に倒す。 */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'nowrap',
        flexShrink: 0,
      }}>
        {visiblePlatforms.map(platform => {
          const value = socialLinks[platform.key];
          if (!value) return null;
          return renderChip(platform, value);
        })}
        {hiddenCount > 0 && (
          <Chip
            label={`+${hiddenCount}`}
            size="small"
            onClick={() => setDialogOpen(true)}
            sx={{
              height: '24px',
              fontSize: '0.7rem',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'text.secondary',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderColor: 'primary.main',
              }
            }}
          />
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
          },
        }}
        sx={{
          '& .MuiPaper-root': {
            margin: { xs: '16px', sm: '32px' },
            width: { xs: 'calc(100% - 32px)', sm: '600px' },
          },
          '& .MuiDialogTitle-root': {
            fontSize: { xs: '0.8rem', sm: '0.9rem' },
            color: 'text.primary',
            p: { xs: 2, sm: 3 },
            pb: { xs: 1.5, sm: 2 },
          },
          '& .MuiDialogContent-root': {
            p: { xs: 2, sm: 3 },
            pt: { xs: 0, sm: 0 },
          }
        }}
      >
        <DialogTitle>ソーシャルリンク</DialogTitle>
        <DialogContent>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            {activePlatforms.map(platform => {
              const value = socialLinks[platform.key];
              if (!value) return null;
              return renderChip(platform, value, true);
            })}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SocialLinks;
